/**
 * API-contract tussen de embed (browser) en ai-photo-style-api (Cloud Run).
 *
 * Endpoints (v1):
 *   GET  /healthz of /v1/health      -> HealthResponse (Cloud Run kan paden op "z" reserveren)
 *   GET  /v1/styles                  -> StylesResponse
 *   POST /v1/nonce                   -> NonceResponse
 *        header X-Turnstile-Token, JSON body NonceRequest
 *   POST /v1/predictions             -> CreatePredictionResponse (201)
 *        header X-Api-Nonce, multipart: style, session_id, coid, image (één bestand);
 *        optioneel openai_billing: sparkles (standaard) of hg (PROVIDER=openai)
 *   GET  /v1/predictions/:id         -> PredictionResponse
 *   POST /v1/predictions/:id/cancel  -> PredictionResponse
 *   GET  /v1/results/:file           -> image/jpeg of image/png (alleen bij lokale storage)
 *
 * Fouten komen altijd terug als ApiError met een passende HTTP-status.
 */

export * from './catalog.js';

export const HEADER_TURNSTILE = 'X-Turnstile-Token';
export const HEADER_NONCE = 'X-Api-Nonce';
/** Alleen actief als de backend met PROVIDER=mock draait. */
export const HEADER_MOCK_SCENARIO = 'X-Mock-Scenario';
/**
 * Alleen voor tests: kiest per aanvraag een andere Replicate-modelpreset (bijv. `flux-2-pro`).
 * De backend negeert hem tenzij ALLOW_MODEL_OVERRIDE=true (nooit in productie).
 */
export const HEADER_MODEL_OVERRIDE = 'X-Aps-Model';

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Kortste zijde van de foto, in pixels. */
export const MIN_IMAGE_SIDE = 512;

export const STYLE_CATALOG = [
  {
    id: 'vintage-waterverf',
    label: 'Foto in watercolor',
    description: 'De foto in watercolor, dezelfde pose en uitsnede.',
  },
  {
    id: 'line-art',
    label: 'Foto in lijntekening',
    description: 'De foto als lijntekening, dezelfde pose en uitsnede.',
  },
  {
    id: 'storybook',
    label: 'Storybook illustration',
    description: 'Zachte gouache in gedempte pastels, als een kinderboek.',
  },
  {
    id: 'cut-paper',
    label: 'Cut-paper collage',
    description: 'Uitgeknipte papieren vormen, plat en grafisch.',
  },
] as const;

export type StyleId = (typeof STYLE_CATALOG)[number]['id'];
export const DEFAULT_STYLE: StyleId = 'vintage-waterverf';

/** Scènes: één foto wordt een nieuwe compositie. Geen vierde stijl. */
export const SCENE_CATALOG = [
  {
    id: 'huisdier-mand',
    label: 'Huisdier bij de mand',
    description: 'Zet alle huisdieren samen op één foto.',
  },
  {
    id: 'ouders-lijn',
    label: 'Ouders in lijn',
    description: 'Fotografeer de ouders samen, het liefst met een beer of pop.',
  },
  {
    id: 'kinderen-wieg',
    label: 'Kinderen bij de wieg',
    description: 'Zet de kinderen van top tot teen op één foto.',
  },
] as const;

export type SceneId = (typeof SCENE_CATALOG)[number]['id'];
/** Stijl of scène, het veld `style` in de generatie-aanvraag. */
export type LookId = string;

/** Welke OpenAI-facturatie bij PROVIDER=openai (meerdere shops, één API). */
export const OPENAI_BILLING_ACCOUNTS = ['sparkles', 'hg'] as const;
export type OpenAiBilling = (typeof OPENAI_BILLING_ACCOUNTS)[number];
export const DEFAULT_OPENAI_BILLING: OpenAiBilling = 'sparkles';

export function isOpenAiBilling(value: string): value is OpenAiBilling {
  return (OPENAI_BILLING_ACCOUNTS as readonly string[]).includes(value);
}

export function styleById(id: string): (typeof STYLE_CATALOG)[number] | undefined {
  return STYLE_CATALOG.find((style) => style.id === id);
}

export function sceneById(id: string): (typeof SCENE_CATALOG)[number] | undefined {
  return SCENE_CATALOG.find((scene) => scene.id === id);
}

export function lookById(id: string): { id: LookId; label: string; description: string } | undefined {
  return styleById(id) ?? sceneById(id);
}

export interface StyleSummary {
  id: StyleId;
  label: string;
  description: string;
}

export interface StylesResponse {
  styles: StyleSummary[];
}

export interface HealthResponse {
  ok: true;
  provider: 'mock' | 'replicate' | 'openai';
  version: string;
  /** Alleen bij PROVIDER=replicate met een ingesteld budget (REPLICATE_MAX_PREDICTIONS). */
  replicate_budget?: { used: number; max: number | null };
  /** Alleen bij PROVIDER=openai met een ingesteld budget (OPENAI_MAX_PREDICTIONS). */
  openai_budget?: { used: number; max: number | null };
}

export interface NonceRequest {
  session_id: string;
  configId?: string;
  shopId?: import('./catalog.js').AiPhotoShopId;
  environmentId?: import('./catalog.js').AiPhotoEnvironmentId;
}

export interface NonceResponse {
  nonce: string;
  /** Only present for a named tool configuration. */
  configId?: string;
  /** ISO-8601 */
  expires_at: string;
  /** Hoeveel generaties er nog met deze nonce gestart mogen worden. */
  uses_left: number;
}

/** Volgt de lifecycle van Replicate, plus `expired` bij onze eigen timeout. */
export type PredictionStatus =
  | 'starting'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'canceled'
  | 'expired';

export const TERMINAL_STATUSES: readonly PredictionStatus[] = [
  'succeeded',
  'failed',
  'canceled',
  'expired',
];

export function isTerminal(status: PredictionStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export interface CreatePredictionResponse {
  prediction_id: string;
  configId?: string;
  status: PredictionStatus;
  /** Advies aan de client: wacht zo lang voor de eerste poll. */
  poll_after_ms: number;
}

export interface PredictionResponse {
  prediction_id: string;
  status: PredictionStatus;
  /**
   * Alleen bij `succeeded`. Kan relatief zijn (`/v1/results/<id>.jpg`);
   * de client lost hem op tegen zijn API-basis-URL.
   */
  result_url?: string;
  result_width?: number;
  result_height?: number;
  /** Stijl of scène waarmee deze generatie is gestart. */
  style?: LookId;
  /** Labelsnapshot en catalogusrevisie, zonder private generatieconfiguratie. */
  style_label?: string;
  catalog_revision?: string;
  configId?: string;
  shopId?: import('./catalog.js').AiPhotoShopId;
  environmentId?: import('./catalog.js').AiPhotoEnvironmentId;
  /** Alleen bij `failed` of `expired`. */
  error?: ApiErrorCode;
  /** ISO-8601 */
  created_at: string;
  /** ISO-8601, zodra de status terminal is. */
  completed_at?: string;
}

export type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'ORIGIN_NOT_ALLOWED'
  | 'TURNSTILE_FAILED'
  | 'NONCE_MISSING'
  | 'NONCE_INVALID'
  | 'NONCE_EXPIRED'
  | 'NONCE_EXHAUSTED'
  | 'SESSION_MISMATCH'
  | 'UNSUPPORTED_STYLE'
  | 'CATALOG_CHANGED'
  | 'CONFIG_NOT_FOUND'
  | 'IMAGE_MISSING'
  | 'IMAGE_TOO_LARGE'
  | 'IMAGE_UNSUPPORTED_TYPE'
  | 'IMAGE_TOO_SMALL'
  | 'RATE_LIMITED'
  | 'NOT_FOUND'
  | 'GENERATION_FAILED'
  | 'GENERATION_TIMEOUT'
  | 'PROVIDER_UNAVAILABLE'
  | 'INTERNAL';

export interface ApiError {
  error: ApiErrorCode;
  message: string;
  /** Bij RATE_LIMITED: na hoeveel seconden opnieuw proberen zin heeft. */
  retry_after_s?: number;
}

export type MockScenario = 'ok' | 'slow' | 'fail' | 'timeout';
